import type { ScanCaptureDetails, ScanOcrParallelShadowEvidence } from "../types/scanWorkflow";
import type { LabelOcrResult } from "./labelOcrService";
import {
  getRecognitionParallelismProbeSnapshot,
  warmRecognitionParallelismProbe,
} from "./recognitionParallelismProbeService";
import type { VisionRuntimeProvenance } from "./visualLabService";
import { evaluateParallelRuntimeAdmission } from "./parallelRuntimeAdmissionService";

const CONTRACT_VERSION = "same-raw-serial-parallel-shadow-v1" as const;
const PROFILE_ID = "ocr-2-worker-2plus2-shadow-v1" as const;
const PRODUCTION_PROFILE = "serial-production-v1" as const;
const SHADOW_YIELD_MS = 250;

let shadowInFlight = false;
let latestShadow: ScanOcrParallelShadowEvidence | null = null;

function nowIso(): string {
  return new Date().toISOString();
}

function upstreamEvidence(scanDetails: ScanCaptureDetails | null) {
  return {
    rawDecodedSha256: scanDetails?.detection?.decodedInputSha256 ?? null,
    analysisInputSha256: scanDetails?.detection?.analysisInputSha256 ?? null,
    rectificationInputRgbaSha256: scanDetails?.rectification?.inputRgbaSha256 ?? null,
    rectificationOutputRgbaSha256: scanDetails?.rectification?.outputRgbaSha256 ?? null,
  };
}

function skippedEvidence(
  reason: string,
  recognitionRunId: string | null,
  scanDetails: ScanCaptureDetails | null,
): ScanOcrParallelShadowEvidence {
  const at = nowIso();
  return {
    schemaVersion: 1,
    contractVersion: CONTRACT_VERSION,
    profileId: PROFILE_ID,
    status: "skipped",
    recognitionRunId,
    startedAtUtc: at,
    finishedAtUtc: at,
    productionInfluenced: false,
    productionExecutionProfile: PRODUCTION_PROFILE,
    upstreamMode: "shared-serial-detection-rectification",
    ...upstreamEvidence(scanDetails),
    rectifiedFileSha256: null,
    requestedWorkers: 2,
    createdWorkers: 0,
    effectiveWorkers: 0,
    activeWorkersPeak: 0,
    workersTerminated: true,
    workersRetainedForReuse: false,
    workerPoolWarm: false,
    workerPoolReused: false,
    workerPoolReuseCount: 0,
    mergeOrder: ["materialNumber", "storageAddress", "materialType", "materialText"],
    workerStartupMs: null,
    serialWallMs: scanDetails?.ocr?.durationMs ?? null,
    parallelWallMs: null,
    estimatedSpeedup: null,
    overlapMs: null,
    overlapRatio: null,
    tasks: [],
    fieldParity: [],
    parity: {
      sameFileObject: true,
      sharedDetectionGeometry: true,
      sharedRectification: true,
      gridAnchoringMatch: null,
      allRoiShaMatch: null,
      allTextMatch: null,
      allConfidenceMatch: null,
      allPassSelectionMatch: null,
      finalFourFieldsMatch: null,
      recognitionParityPass: null,
    },
    fallbackReason: reason,
    error: null,
  };
}

export function getLatestRecognitionParallelShadowEvidence(): ScanOcrParallelShadowEvidence | null {
  return latestShadow ? structuredClone(latestShadow) : null;
}

export async function scheduleRecognitionParallelShadow(input: {
  file: File;
  serialResult: LabelOcrResult;
  scanDetails: ScanCaptureDetails | null;
  runtimeSnapshot?: Pick<VisionRuntimeProvenance, "ocr">;
  recognitionRunId?: string | null;
}): Promise<ScanOcrParallelShadowEvidence> {
  const recognitionRunId = input.recognitionRunId ?? null;
  if (shadowInFlight) {
    latestShadow = skippedEvidence("SHADOW_BUSY", recognitionRunId, input.scanDetails);
    return structuredClone(latestShadow);
  }

  // Production result is already final at this point. Yield so Step 2 can render
  // before the optional research work competes for browser CPU/memory.
  await new Promise<void>((resolve) => window.setTimeout(resolve, SHADOW_YIELD_MS));

  if (typeof document !== "undefined" && document.visibilityState !== "visible") {
    latestShadow = skippedEvidence("DOCUMENT_NOT_VISIBLE", recognitionRunId, input.scanDetails);
    return structuredClone(latestShadow);
  }

  const admission = evaluateParallelRuntimeAdmission();
  if (!admission.allowed) {
    latestShadow = skippedEvidence(`PV7_ADMISSION_${admission.reason ?? "DENIED"}`, recognitionRunId, input.scanDetails);
    return structuredClone(latestShadow);
  }

  const probe = await warmRecognitionParallelismProbe().catch(() => getRecognitionParallelismProbeSnapshot());
  if (
    probe.status !== "ready"
    || probe.classification !== "likely_parallel"
    || probe.checksumMatch !== true
    || probe.createdWorkers < 2
  ) {
    latestShadow = skippedEvidence(
      probe.status === "unsupported"
        ? (probe.fallbackReason ?? "PARALLELISM_UNSUPPORTED")
        : probe.status === "error"
          ? (probe.fallbackReason ?? "PARALLELISM_PROBE_ERROR")
          : `PARALLELISM_GATE_${probe.classification.toUpperCase()}`,
      recognitionRunId,
      input.scanDetails,
    );
    return structuredClone(latestShadow);
  }

  if (shadowInFlight) {
    latestShadow = skippedEvidence("SHADOW_BUSY_AFTER_GATE", recognitionRunId, input.scanDetails);
    return structuredClone(latestShadow);
  }

  shadowInFlight = true;
  try {
    const { runLabelOcrParallelShadowCandidate } = await import("./labelOcrService");
    const candidate = await runLabelOcrParallelShadowCandidate(
      input.file,
      input.serialResult,
      input.runtimeSnapshot,
      { recognitionRunId },
    ) as unknown as ScanOcrParallelShadowEvidence;
    latestShadow = {
      ...candidate,
      ...upstreamEvidence(input.scanDetails),
      productionInfluenced: false,
      productionExecutionProfile: PRODUCTION_PROFILE,
      upstreamMode: "shared-serial-detection-rectification",
    };
    return structuredClone(latestShadow);
  } catch (error: unknown) {
    const fallback = skippedEvidence("SHADOW_SCHEDULER_ERROR", recognitionRunId, input.scanDetails);
    latestShadow = {
      ...fallback,
      status: "error",
      error: error instanceof Error ? error.message : String(error),
    };
    return structuredClone(latestShadow);
  } finally {
    shadowInFlight = false;
  }
}
